import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Subscription, interval, startWith, switchMap } from 'rxjs';

export interface WifiDevice {
  ip: string;
  mac: string | null;
  vendor: string;
  classification?: string;
  mac_type?: string;
  private_mac?: boolean;
  sources?: string[];
  vendor_known?: boolean;
  vendor_source?: string | null;
}

export interface ScanResponse {
  mobile_devices: WifiDevice[];
  possible_mobile_devices: WifiDevice[];
  private_mac_devices: WifiDevice[];
  unknown_vendor_devices: WifiDevice[];
}

@Component({
  selector: 'app-wifi-devices',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './wifi-devices.component.html',
  styleUrl: './wifi-devices.component.css'
})
export class WifiDevicesComponent implements OnInit, OnDestroy {
  mobileDevices: WifiDevice[] = [];
  possibleMobileDevices: WifiDevice[] = [];
  privateMacDevices: WifiDevice[] = [];
  unknownVendorDevices: WifiDevice[] = [];

  isLoading: boolean = true;
  isError: boolean = false;
  errorMessage: string = '';
  searchQuery: string = '';

  expandedSections: { [key: string]: boolean } = {
    mobile: true,
    possible: false,
    privateMac: false,
    unknown: false
  };

  private pollSubscription?: Subscription;

  constructor(private http: HttpClient) { }

  ngOnInit(): void {
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  startPolling(): void {
    this.stopPolling();

    this.pollSubscription = interval(15000)
      .pipe(
        startWith(0),
        switchMap(() => {
          return this.http.get<ScanResponse>('https://6f8bm12t-8000.inc1.devtunnels.ms/scan');
        })
      )
      .subscribe({
        next: (data) => {
          this.isLoading = false;
          this.isError = false;
          if (data) {
            this.mobileDevices = data.mobile_devices || [];
            this.possibleMobileDevices = data.possible_mobile_devices || [];
            this.privateMacDevices = data.private_mac_devices || [];
            this.unknownVendorDevices = data.unknown_vendor_devices || [];
          } else {
            this.mobileDevices = [];
            this.possibleMobileDevices = [];
            this.privateMacDevices = [];
            this.unknownVendorDevices = [];
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.isError = true;
          this.errorMessage = 'Failed to connect to scanner API. Retrying...';
          console.error('Scan API error:', err);
        }
      });
  }

  stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = undefined;
    }
  }

  getFilteredList(devices: WifiDevice[]): WifiDevice[] {
    if (!this.searchQuery) {
      return devices;
    }
    const query = this.searchQuery.toLowerCase().trim();
    return devices.filter(device =>
      (device.ip && device.ip.toLowerCase().includes(query)) ||
      (device.mac && device.mac.toLowerCase().includes(query)) ||
      (device.vendor && device.vendor.toLowerCase().includes(query))
    );
  }

  getFilteredDevices(): WifiDevice[] {
    return [
      ...this.getFilteredList(this.mobileDevices),
      ...this.getFilteredList(this.possibleMobileDevices),
      ...this.getFilteredList(this.privateMacDevices),
      ...this.getFilteredList(this.unknownVendorDevices)
    ];
  }

  toggleSection(section: string): void {
    this.expandedSections[section] = !this.expandedSections[section];
  }

  getVendorBadgeClass(vendor: string): string {
    if (!vendor) return 'badge-generic';
    const v = vendor.toLowerCase();
    if (v.includes('apple')) return 'badge-apple';
    if (v.includes('samsung')) return 'badge-samsung';
    if (v.includes('google')) return 'badge-google';
    if (v.includes('vivo')) return 'badge-vivo';
    if (v.includes('xiaomi')) return 'badge-xiaomi';
    return 'badge-generic';
  }

  getVendorType(vendor: string): string {
    if (!vendor) return 'generic';
    const v = vendor.toLowerCase();
    if (v.includes('apple')) return 'apple';
    if (v.includes('vivo')) return 'vivo';
    if (v.includes('samsung')) return 'samsung';
    if (v.includes('xiaomi')) return 'xiaomi';
    return 'generic';
  }

  getVendorTypeClass(vendor: string): string {
    return 'avatar-' + this.getVendorType(vendor);
  }

  getShortVendor(vendor: string): string {
    if (!vendor) return 'Generic';
    const v = vendor.toLowerCase();
    if (v.includes('apple')) return 'Apple';
    if (v.includes('vivo')) return 'Vivo';
    if (v.includes('samsung')) return 'Samsung';
    if (v.includes('xiaomi')) return 'Xiaomi';
    const parts = vendor.split(/[\s,.]+/);
    return parts[0] || vendor;
  }
}
